import { createSelector } from 'reselect'
import { type Address, getAddress } from 'viem'
import { zeroAddress } from 'viem'

import { type ChainIdParameter } from '@wagmi/core/internal'
import { type Config } from 'wagmi'
import { getEntities } from '../../actions/getLabels'
import { getProducts } from '../../actions/getProducts'
import { GetGenericVaultData, isClassicVault, isManagedVault } from '../../hooks'
import { GovernorInfo, GovernorType, type VaultGovernors } from './types'

export type GetProductForVaultParameters<config extends Config = Config> = ChainIdParameter<config> & {
  vaultAddress?: Address
}

export function selectVaultMarket<config extends Config = Config>({
  chainId,
  vaultAddress,
}: GetProductForVaultParameters<config>) {
  // Products data is static, so it's fine to pull it here
  const products = getProducts({ chainId })

  if (!products) return undefined

  const productEntry =
    vaultAddress &&
    [...products.entries()].find(
      ([, product]) => product.vaults.includes(vaultAddress) || product.deprecatedVaults?.includes(vaultAddress),
    )

  if (!productEntry) return undefined

  return {
    key: productEntry[0],
    ...productEntry[1],
  }
}

const createGovernorInfo = (
  key: string,
  name: string,
  logo: string,
  address: Address,
  type: GovernorType = GovernorType.KNOWN,
): GovernorInfo => ({
  key,
  name,
  logo: `/${logo}`,
  address: getAddress(address),
  type,
})

const createUnknownGovernorInfo = (address: Address): GovernorInfo => ({
  address: getAddress(address),
  type: GovernorType.UNKNOWN,
})

const findMatchingGovernors = (entities: ReturnType<typeof getEntities>, governorAddress: Address) => {
  if (!entities) return []
  return [...entities.entries()].filter(([, { addresses = {} }]) => Object.keys(addresses).includes(governorAddress))
}

const createOptimisticGovernors = (
  entities: ReturnType<typeof getEntities>,
  entityKeys: readonly string[],
): GovernorInfo[] => {
  const optimisticGovernors: GovernorInfo[] = []

  if (!entities) return optimisticGovernors

  for (const entityKey of entityKeys) {
    const entity = entities.get(entityKey)
    if (entity) {
      const entityAddresses = entity.addresses ? Object.keys(entity.addresses) : []
      if (entityAddresses.length > 0) {
        optimisticGovernors.push(
          createGovernorInfo(
            entityKey,
            entity.name,
            entity.logo,
            // @ts-ignore - entityAddresses[0] might be undefined
            getAddress(entityAddresses[0]),
          ),
        )
      }
    }
  }

  return optimisticGovernors
}

const processGovernorAddresses = (
  governorAddresses: readonly Address[],
  entities: ReturnType<typeof getEntities>,
): GovernorInfo[] => {
  const allGovernors: GovernorInfo[] = []

  if (!entities) {
    return governorAddresses.map((address) => createUnknownGovernorInfo(address))
  }

  for (const governorAddress of governorAddresses) {
    const matchingGovernors = findMatchingGovernors(entities, governorAddress)

    if (matchingGovernors.length > 0) {
      allGovernors.push(
        ...matchingGovernors.map(([key, { name, logo }]) => createGovernorInfo(key, name, logo, governorAddress)),
      )
    } else {
      allGovernors.push(createUnknownGovernorInfo(governorAddress))
    }
  }

  return allGovernors
}

export const selectGovernors = createSelector(
  [
    (vault: NonNullable<GetGenericVaultData>) => vault,
    (_, entities?: ReturnType<typeof getEntities>) => entities,
    (_, __, market?: ReturnType<typeof selectVaultMarket>) => market,
  ],
  (vault, entities, market): VaultGovernors => {
    if (!vault)
      return {
        governors: [{ address: zeroAddress, type: GovernorType.UNGOVERNED }],
      }

    let governorAddresses: readonly Address[] = []

    if (isClassicVault(vault)) {
      const governorAdmin = vault.governorAdmin
      governorAddresses = governorAdmin === zeroAddress ? [] : [governorAdmin]
    } else if (isManagedVault(vault)) {
      governorAddresses = [getAddress(vault.owner || zeroAddress)]
    }

    return selectGovernorsFromAddresses(governorAddresses, entities, market)
  },
)

export const selectGovernorsFromAddresses = createSelector(
  [
    (addresses: Address[] | Address | undefined) => addresses,
    (_, entities?: ReturnType<typeof getEntities>) => entities,
    (_, __, market?: ReturnType<typeof selectVaultMarket>) => market,
  ],
  (addresses, entities, market): VaultGovernors => {
    if (!addresses)
      return {
        governors: [{ address: zeroAddress, type: GovernorType.UNGOVERNED }],
      }

    let governorAddresses: readonly Address[] = []

    if (Array.isArray(addresses)) {
      governorAddresses = addresses.filter((addr): addr is Address => addr && addr !== zeroAddress).map(getAddress)
    } else if (addresses && addresses !== zeroAddress) {
      governorAddresses = addresses && addresses !== zeroAddress ? [getAddress(addresses)] : []
    }

    // Optimistically display governors from product data if available
    let optimisticGovernors: GovernorInfo[] = []
    let hasGovernorAddressMismatch = false

    if (entities && market) {
      const entityKeys = Array.isArray(market.entity) ? market.entity : [market.entity]

      optimisticGovernors = createOptimisticGovernors(entities, entityKeys)
    }

    if (entities && !market) {
      // When no market is available, check if governor addresses exist in entities
      // Skip optimistic logic since we don't have market data
      if (governorAddresses.length > 0) {
        const allGovernors = processGovernorAddresses(governorAddresses, entities)
        return { governors: allGovernors }
      }

      // If no governor addresses, return ungoverned
      return {
        governors: [{ address: zeroAddress, type: GovernorType.UNGOVERNED }],
      }
    }

    // If no governor addresses from vault data but we have optimistic governors, use them
    if (!governorAddresses.length) {
      if (optimisticGovernors.length > 0) {
        return { governors: optimisticGovernors }
      }
      return {
        governors: [{ address: zeroAddress, type: GovernorType.UNGOVERNED }],
      }
    }

    // Check for mismatches between vault data and entity addresses
    if (entities && optimisticGovernors.length > 0) {
      const entityAddresses = new Set(
        optimisticGovernors.flatMap((governor) => {
          const entity = entities.get(governor.key || '')
          return entity?.addresses ? Object.keys(entity.addresses) : []
        }),
      )

      // Check if any vault governor address doesn't match entity addresses
      const vaultGovernorAddresses = governorAddresses.map((addr) => getAddress(addr))

      hasGovernorAddressMismatch = !vaultGovernorAddresses.some((addr) => entityAddresses.has(addr))
    }

    if (entities) {
      let allGovernors: GovernorInfo[] = []

      // If there's a mismatch, don't include governors with addresses
      if (hasGovernorAddressMismatch) {
        // Only include derived governors from market data (without addresses)
        if (market) {
          const entityKeys = Array.isArray(market.entity) ? market.entity : [market.entity]

          // Get all entities to use as governors (without addresses)
          for (const entityKey of entityKeys) {
            const entity = entities.get(entityKey)
            if (entity) {
              allGovernors.push({
                key: entityKey,
                name: entity.name,
                logo: `/${entity.logo}`,
                type: GovernorType.KNOWN,
              })
            }
          }
        }
      } else {
        // No mismatch, include all governors with addresses
        allGovernors = processGovernorAddresses(governorAddresses, entities)
      }

      // Add warning flag to the result
      const result: VaultGovernors = {
        governors: allGovernors,
      }
      if (hasGovernorAddressMismatch) {
        result.hasGovernorAddressMismatch = true
      }

      return result
    }

    const result: VaultGovernors = {
      governors: governorAddresses.map((address) => createUnknownGovernorInfo(address)),
    }

    if (hasGovernorAddressMismatch) {
      result.hasGovernorAddressMismatch = true
    }

    return result
  },
)
